import { AlertTriangle, Home, RotateCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useI18n } from "@/hooks/useI18n";

interface ErrorPageProps {
  /**
   * Azione del pulsante. Di default ricarica la pagina: la fallback è mostrata
   * dopo un crash, quindi ripartire da zero è più affidabile che rimontare
   * l'albero rotto.
   */
  onRetry?: () => void;
}

/**
 * Schermata mostrata quando l'app va in errore irrecuperabile.
 * Renderizzata da ErrorBoundary (fuori dai provider) e raggiungibile su /error:
 * non deve dipendere da router, auth o altri context. i18next è inizializzato
 * in main.tsx, quindi useI18n qui è sicuro.
 */
const ErrorPage = ({ onRetry }: ErrorPageProps) => {
  const { t } = useI18n();

  const handleRetry = () => {
    if (onRetry) {
      onRetry();
      return;
    }
    window.location.reload();
  };

  // Niente <Link>: la pagina può essere renderizzata fuori dal Router. L'app usa
  // HashRouter, quindi si imposta l'hash e si ricarica — il reload serve anche
  // quando si è già su /dashboard, altrimenti l'albero rotto resterebbe montato.
  const handleGoHome = () => {
    window.location.hash = "#/dashboard";
    window.location.reload();
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex items-center justify-center px-6">
      <div className="w-full max-w-xl rounded-2xl border border-slate-200 bg-white shadow-sm p-8 sm:p-10 text-center">
        <div className="mx-auto mb-5 h-14 w-14 rounded-full bg-amber-100 flex items-center justify-center">
          <AlertTriangle className="h-7 w-7 text-amber-700" />
        </div>

        <h1 className="text-slate-900 mb-4">{t("errors.crashTitle")}</h1>

        <p className="text-slate-600 leading-relaxed mb-8">
          {t("errors.crashDescription")}
        </p>

        <div className="flex flex-col items-center gap-3">
          <Button onClick={handleRetry} className="h-11 px-7 gap-2">
            <RotateCw className="h-4 w-4" />
            {t("errors.crashRefresh")}
          </Button>

          <Button variant="ghost" onClick={handleGoHome} className="h-11 px-7 gap-2">
            <Home className="h-4 w-4" />
            {t("errors.crashGoHome")}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ErrorPage;
